import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import { FormikProvider, useFormik } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Card,
  Table,
  Stack,
  Avatar,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Checkbox,
  // InputAdornment,
  Skeleton
} from '@material-ui/core';
import Scrollbar from 'src/components/Scrollbar';
import ConfirmationPopup from '../official-request/ConfirmationPopup';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { REST_API_END_POINT, UNITS } from 'src/constants/Defaultvalues';
import moment from 'moment';
import QbLogo from 'src/assets/quickbookLogo-removebg-preview.png';
import { Info } from '@material-ui/icons';

const QUICKBOOKS_JOB_COST_INFO =
  '1. When a job is in progress in QuickBooks, the amount should be debited to Work In Progress(1014000) and credited to (Inventory(1013000) / Split Labor(5081000)). ' +
  '2. When the job is completed, the total job cost amount should be debited to Finished Goods(1014300) and credited to Work In Progress(1014000).';
import { getJobCostMaterialMarkupPercent } from 'src/utils/jobCostMaterialCalc';

const preventInvalidNumberKeys = (event) => {
  if (['-', '+', 'e', 'E'].includes(event.key)) {
    event.preventDefault();
  }
};


const TABLE_HEAD = [
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'item', label: 'Item', align: 'left' },
  { id: 'Expt Qty', label: 'Expt Qty', align: 'center' },
  { id: 'qty', label: 'Qty', align: 'center' },
  { id: 'unitCost', label: 'Unit Cost', align: 'left' },
  { id: 'markup', label: 'Markup %', align: 'left' },
  { id: 'billRate', label: 'Bill Rate', align: 'left' },
  { id: 'cost', label: 'Cost', align: 'left' },
  { id: 'bill', label: 'Bill', align: 'left' },
  { id: 'billable', label: 'Billable', align: 'center' },
  { id: 'actions', label: 'Actions', align: 'center' }
];

const PRICE_SOURCE = {
  MARKUP: 'markup',
  BILL_RATE: 'bill_rate'
};

const toSafeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const round2 = (value) => Math.round((toSafeNumber(value) + Number.EPSILON) * 100) / 100;

const normalizeBillable = (value) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  value === 'true';

const calculateMargin = (total_bill, total_cost) => {
  const safeTotalBill = toSafeNumber(total_bill);
  const safeTotalCost = toSafeNumber(total_cost);

  if (!safeTotalBill || safeTotalBill === 0) {
    if (safeTotalCost > 0) return -100;
    return 0;
  }

  return round2(((safeTotalBill - safeTotalCost) / safeTotalCost) * 100);
};

// Backup before T&M material markup: const calculateRowValues = ({ qty, unitCost, markup, billRate, billable, priceSource }) => {
const calculateRowValues = ({ qty, unitCost, markup, billRate, billable, priceSource, costIncludesMarkup = false }) => {
  const safeQty = toSafeNumber(qty);
  const safeUnitCost = toSafeNumber(unitCost);
  let safeMarkup = toSafeNumber(markup);
  let safeBillRate = toSafeNumber(billRate);
  const safeBillable = normalizeBillable(billable);
  const safePriceSource = priceSource === PRICE_SOURCE.BILL_RATE ? PRICE_SOURCE.BILL_RATE : PRICE_SOURCE.MARKUP;

  if (safePriceSource === PRICE_SOURCE.MARKUP) {
    safeBillRate = safeUnitCost * (1 + safeMarkup / 100);
  } else {
    const rawMarkup = safeUnitCost === 0 ? toSafeNumber(markup) : ((safeBillRate - safeUnitCost) / safeUnitCost) * 100;
    
    // Heuristic: Prefer "cleaner" markups (integer or 1-decimal) if they produce the same rounded bill rate.
    // This resolves the discrepancy where e.g. 10% -> $61.57, but $61.57 -> 10.01%.
    const roundedInt = Math.round(rawMarkup);
    const rounded1 = Math.round(rawMarkup * 10) / 10;
    const currentBillRate = round2(safeBillRate);

    if (round2(safeUnitCost * (1 + roundedInt / 100)) === currentBillRate) {
      safeMarkup = roundedInt;
    } else if (round2(safeUnitCost * (1 + rounded1 / 100)) === currentBillRate) {
      safeMarkup = rounded1;
    } else {
      safeMarkup = rawMarkup;
    }
  }

  // Use rounded values for totals to ensure consistency with what is displayed in the UI.
  const finalQty = round2(safeQty);
  const finalUnitCost = round2(safeUnitCost);
  const finalBillRate = round2(safeBillRate);

  // Backup before T&M material markup: const safeCost = finalQty * finalUnitCost;
  const safeCost = finalQty * (costIncludesMarkup ? finalBillRate : finalUnitCost);
  const safeBill = safeBillable ? finalQty * finalBillRate : 0;

  return {
    qty: finalQty,
    unitCost: finalUnitCost,
    markup: round2(safeMarkup),
    billRate: finalBillRate,
    billable: safeBillable,
    cost: round2(safeCost),
    bill: round2(safeBill)
  };
};

const getMarkupTooltip = (unitCost, billRate, markup) =>
  `Markup % = ((Bill Rate - Unit Cost) / Unit Cost) x 100 = (($${toSafeNumber(billRate).toFixed(2)} - $${toSafeNumber(unitCost).toFixed(2)}) / $${toSafeNumber(unitCost).toFixed(2)}) x 100 = ${toSafeNumber(markup).toFixed(2)}%`;

const getBillRateTooltip = (unitCost, markup, billRate) =>
  `Bill Rate = Unit Cost x (1 + (Markup % / 100)) = $${toSafeNumber(unitCost).toFixed(2)} x (1 + (${toSafeNumber(markup).toFixed(2)} / 100)) = $${toSafeNumber(billRate).toFixed(2)}`;

const getCostTooltip = (qty, unitCost, cost, markup = 0, isTM = false) => {
  // Backup before T&M cost tooltip markup explanation:
  // return `Cost = Qty x Unit Cost = ${toSafeNumber(qty).toFixed(2)} x $${toSafeNumber(unitCost).toFixed(2)} = $${toSafeNumber(cost).toFixed(2)}`;
  if (isTM) {
    return `Cost = Qty x (Unit Cost x (1 + (Markup % / 100))) = ${toSafeNumber(qty).toFixed(2)} x ($${toSafeNumber(unitCost).toFixed(2)} x (1 + (${toSafeNumber(markup).toFixed(2)} / 100))) = $${toSafeNumber(cost).toFixed(2)}`;
  }
  return `Cost = Qty x Unit Cost = ${toSafeNumber(qty).toFixed(2)} x $${toSafeNumber(unitCost).toFixed(2)} = $${toSafeNumber(cost).toFixed(2)}`;
};

const getBillTooltip = (qty, billRate, billable, bill) =>
  normalizeBillable(billable)
    ? `Bill = Qty x Bill Rate = ${toSafeNumber(qty).toFixed(2)} x $${toSafeNumber(billRate).toFixed(2)} = $${toSafeNumber(bill).toFixed(2)}`
    : `Bill = 0 (Billable is false)`;

// Align with recipeCost.js materials: expected = jobcost_actual_quantity, actual = jobcost_quantity; one editable qty uses effective display.
const getExpectedQtyMaterial = (item) => toSafeNumber(item?.jobcost_actual_quantity ?? item?.quantity);
const formatExpectedQtyDisplay = (item) => {
  const raw = item?.match_actual_quantity ?? item?.jobcost_actual_quantity;
  if (raw == null || raw === '') return '-';
  return raw;
};
const getActualQtyMaterial = (item) => toSafeNumber(item?.jobcost_quantity);
const getEffectiveQtyMaterial = (item) => {
  const actual = item?.jobcost_quantity;
  if (actual != null && actual !== '') return toSafeNumber(actual);
  return getExpectedQtyMaterial(item);
};

// Same resolution pattern as JobCostEquipmentsTab (cost / bill rates from row + totals ÷ qty).
const getEffectiveUnitCostMaterial = (item, qty) => {
  // 1. Priority: Direct Overrides (Unit Cost or Actual Unit Cost)
  const direct = item?.jobcost_unit_cost;
  if (direct != null && direct !== '' && toSafeNumber(direct) > 0) return toSafeNumber(direct);

  const actualDirect = item?.jobcost_actual_unit_cost;
  if (actualDirect != null && actualDirect !== '' && toSafeNumber(actualDirect) > 0) return toSafeNumber(actualDirect);

  // 2. Priority: Saved Job Cost Totals (Actual Total first, then standard Total)
  const actualTotal = item?.jobcost_actual_total;
  if (actualTotal != null && actualTotal !== '' && toSafeNumber(actualTotal) > 0) {
    const safeQty = toSafeNumber(qty);
    return safeQty > 0 ? toSafeNumber(actualTotal) / safeQty : 0;
  }

  const jobcostTotal = item?.jobcost_total;
  if (jobcostTotal != null && jobcostTotal !== '' && toSafeNumber(jobcostTotal) > 0) {
    const safeQty = toSafeNumber(qty);
    return safeQty > 0 ? toSafeNumber(jobcostTotal) / safeQty : 0;
  }

  // 3. Fallback: Original Prices
  const uc = item?.unit_cost;
  if (uc != null && uc !== '' && toSafeNumber(uc) > 0) return toSafeNumber(uc);
  const price = item?.price;
  if (price != null && price !== '' && toSafeNumber(price) > 0) return toSafeNumber(price);

  // Separate check for intentional absolute 0 if jobcost entry exists
  if ((direct === 0 || direct === '0') || (actualDirect === 0 || actualDirect === '0')) return 0;

  return 0;
};

const getEffectiveBillRateMaterial = (item, qty) => {
  // 1. Priority: Direct Override Bill Rate
  const direct = item?.jobcost_bill_rate;
  if (direct != null && direct !== '' && toSafeNumber(direct) > 0) return toSafeNumber(direct);

  // 2. Priority: Saved Job Cost Total Bill
  const totalBill = item?.jobcost_total_bill;
  if (totalBill != null && totalBill !== '' && toSafeNumber(totalBill) > 0) {
    const safeQty = toSafeNumber(qty);
    return safeQty > 0 ? toSafeNumber(totalBill) / safeQty : 0;
  }

  // 3. Fallback: Original / Default Bill Rates
  const br = item?.bill_rate;
  if (br != null && br !== '' && toSafeNumber(br) > 0) return toSafeNumber(br);

  if (direct === 0 || direct === '0') return 0;

  return 0;
};

const getBillableTooltipMaterial = (billable) =>
  normalizeBillable(billable)
    ? 'Billable is true, so Bill uses Qty x Bill Rate'
    : 'Billable is false, so Bill is 0';

export default function JobCostMaterialsTab({
  selectedProjects,
  setSelectedProjects,
  additionalMaterials,
  theme,
  initialMaterials = [],
  productList = [],
  isLoading = false,
  onAddMaterialEntry,
  onSummaryChange,
  projectId,
  userId,
  onMaterialsRefresh,
  isMirrored = false,
  projectData,
  isTM = false
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [editingUid, setEditingUid] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [lastEditedPriceField, setLastEditedPriceField] = useState(PRICE_SOURCE.MARKUP);
  const [savingAdditional, setSavingAdditional] = useState(false);
  const materialsCount = selectedProjects?.length || 0;
  const additionalMaterialsCount = additionalMaterials?.length || 0;

  const mappedRows = useMemo(() => {
    const materials = (selectedProjects || []).map((item, index) => {
      const serviceQty = getEffectiveQtyMaterial(item);
      const unitCost = getEffectiveUnitCostMaterial(item, serviceQty);
      const billRate = getEffectiveBillRateMaterial(item, serviceQty);
      // Backup before T&M material markup: markup was item?.retail_price ?? item?.markup ?? 0.
      const markup = getJobCostMaterialMarkupPercent(item);
      const calculated = calculateRowValues({
        qty: serviceQty,
        unitCost,
        // markup: item?.markup ?? 0,
        // Backup before T&M material markup: markup: item?.retail_price ?? item?.markup ?? 0,
        markup,
        billRate,
        billable: item?.billable ?? item?.is_billable ?? true,
        // Backup before T&M material markup: priceSource: PRICE_SOURCE.BILL_RATE
        priceSource: isTM ? PRICE_SOURCE.MARKUP : PRICE_SOURCE.BILL_RATE,
        costIncludesMarkup: isTM
      });

      return {
        uid: `material-${index}-${item?.id || item?.product_id || index}`,
        type: 'material',
        sourceIndex: index,
        raw: item,
        date: item?.jobcost_timestamp ? moment.unix(item.jobcost_timestamp).format('DD/MM/YYYY') : (item?.date || "-"),
        item: item?.productNotes || item?.name || item?.productName || item?.rfpProductName || 'Material',
        barcode: item?.barcode || '',
        qty: calculated.qty,
        unitCost: calculated.unitCost,
        // markup: calculated.markup,
        // Backup before T&M material markup: markup: item?.retail_price || item?.markup || 0,
        markup,
        billRate: calculated.billRate,
        cost: calculated.cost,
        bill: calculated.bill,
        billable: calculated.billable
      };
    });

    const additionals = (additionalMaterials || []).map((item, index) => {
      const qty = toSafeNumber(item?.quantity ?? 0);
      const unitCost = toSafeNumber(
        (item?.unit_cost && Number(item.unit_cost) > 0) ? item.unit_cost : 
        (item?.match_actual_unit_price && Number(item.match_actual_unit_price) > 0) ? item.match_actual_unit_price :
        item?.price ?? 0
      );
      // let markup = toSafeNumber(item?.markup_percent ?? item?.markup ?? 0);
      // Backup before T&M material markup: let markup = item?.retail_price || item?.markup_percent || item?.markup || 0;
      let markup = getJobCostMaterialMarkupPercent(item);
      const billRate = toSafeNumber(item?.bill_unit_price ?? item?.jobcost_bill_rate ?? 0);
      const billableRaw = item?.billable;
      const billable =
        billableRaw !== 0 && billableRaw !== '0' && billableRaw !== false;
      const calculated = calculateRowValues({
        qty,
        unitCost,
        markup,
        billRate,
        billable,
        // Backup before T&M material markup: priceSource: PRICE_SOURCE.BILL_RATE
        priceSource: isTM ? PRICE_SOURCE.MARKUP : PRICE_SOURCE.BILL_RATE,
        costIncludesMarkup: isTM
      });
      const uid = `additional-${index}-${item?.job_cost_id ?? item?.product_id ?? item?.id ?? index}`;

      return {
        uid,
        type: 'additional',
        sourceIndex: index,
        raw: item,
        date: item?.jobcost_timestamp ? moment.unix(item.jobcost_timestamp).format('DD/MM/YYYY') : (item?.date || "-"),
        item: item?.productNotes || item?.name || 'Additional Material',
        barcode: item?.barcode || '',
        qty: calculated.qty,
        unitCost: calculated.unitCost,
        // markup: calculated.markup,
        markup: markup,
        billRate: calculated.billRate,
        cost: calculated.cost,
        bill: calculated.bill,
        billable: calculated.billable
      };
    });

    return [...materials, ...additionals];
  // Backup before T&M material markup: }, [selectedProjects, additionalMaterials]);
  }, [selectedProjects, additionalMaterials, isTM]);

  useEffect(() => {
    setRows(mappedRows);
  }, [mappedRows]);

  const summaryStats = useMemo(() => {
    const totalCost = rows.reduce((sum, row) => sum + (Number(row.cost) || 0), 0);
    const totalBillable = rows.reduce((sum, row) => sum + (row.billable ? Number(row.bill) || 0 : 0), 0);
    const marginPercent = calculateMargin(totalBillable, totalCost);
    return { totalCost, totalBillable, marginPercent };
  }, [rows]);

  useEffect(() => {
    if (onSummaryChange) {
      onSummaryChange({
        cost: Number(summaryStats.totalCost || 0),
        billable: Number(summaryStats.totalBillable || 0),
        marginPercent: Number(summaryStats.marginPercent || 0)
      });
    }
  }, [summaryStats, onSummaryChange]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      item: '',
      qty: 0,
      unitCost: 0,
      markup: 0,
      billRate: 0,
      billable: true
    },
    onSubmit: async (values) => {
      if (!editingUid) return;

      const row = rows.find((r) => r.uid === editingUid);
      if (!row) return;

      const isMaterialType = row.type === 'material';
      const isAdditionalType = row.type === 'additional';

      if (isAdditionalType || (isMaterialType && projectId && userId)) {
        if (isMirrored) {
          enqueueSnackbar('Cannot edit in mirrored view', { variant: 'warning' });
          return;
        }
        
        const jobCostId = isAdditionalType 
          ? (row.raw?.job_cost_id ?? row.raw?.product_id) 
          : (row.raw?.job_cost_id ?? row.raw?.id);
        if (!jobCostId) {
          enqueueSnackbar('Job cost id missing — refresh and try again', { variant: 'error' });
          return;
        }

        const calculated = calculateRowValues({
          qty: values.qty,
          unitCost: values.unitCost,
          markup: values.markup,
          billRate: values.billRate,
          billable: values.billable,
          // Backup before T&M material markup: priceSource: lastEditedPriceField
          priceSource: lastEditedPriceField,
          costIncludesMarkup: isTM
        });

        setSavingAdditional(true);
        try {
          const endpoint = isAdditionalType 
            ? `${REST_API_END_POINT}project/save-job-cost-tm-additional-material`
            : `${REST_API_END_POINT}project/save-job-cost-material`;

          const res = await axios.post(
            endpoint,
            {
              jobCostId,
              projectId,
              userId,
              materialLabel: values.item,
              barcode:row?.barcode || '',
              quantity: calculated.qty,
              unitCost: calculated.unitCost,
              markupPercent: calculated.markup,
              billUnitPrice: calculated.billRate,
              billable: calculated.billable,
              jobcost_timestamp: Math.floor(Date.now() / 1000)
            },
            { headers: { 'Content-Type': 'application/json' } }
          );
          if (res?.data?.status === 1) {
            enqueueSnackbar(res.data.message || 'Saved', { variant: 'success' });
            setEditingUid(null);
            if (onMaterialsRefresh) onMaterialsRefresh();
          } else {
            enqueueSnackbar(res?.data?.message || 'Save failed', { variant: 'error' });
          }
        } catch (err) {
          enqueueSnackbar(err?.message || 'Save failed', { variant: 'error' });
        } finally {
          setSavingAdditional(false);
        }
        return;
      }

      if (!isMaterialType) return;

      const raw = row.raw || {};
      let inputQty = toSafeNumber(values.qty);

      if (
        !isTM &&
        raw?.available_stock != null &&
        Number(raw.available_stock) > 0 &&
        inputQty > Number(raw.available_stock)
      ) {
        enqueueSnackbar(
          `Stock not available. Available stock: ${raw.available_stock}`,
          { variant: 'error' }
        );
        setSelectedProjects((prev) =>
          prev.map((project, i) =>
            i === row.sourceIndex ? { ...project, taken: 0 } : project
          )
        );
        return;
      }

      const maxAllowedValue = Number(raw?.jobcost_actual_quantity);
      const constrainedValue =
        maxAllowedValue > 0 ? Math.min(inputQty, maxAllowedValue) : inputQty;
      // const taken =
      //   maxAllowedValue > 0
      //     ? Math.min(inputQty, maxAllowedValue) === maxAllowedValue
      //       ? 1
      //       : 0
      //     : toSafeNumber(raw?.taken);
        const taken = 1;

      const finalQty = round2(constrainedValue);

      const calculated = calculateRowValues({
        qty: finalQty,
        unitCost: values.unitCost,
        markup: values.markup,
        billRate: values.billRate,
        billable: values.billable,
        // Backup before T&M material markup: priceSource: lastEditedPriceField
        priceSource: lastEditedPriceField,
        costIncludesMarkup: isTM
      });

      setSelectedProjects((prevProjects) =>
        prevProjects.map((project, index) =>
          index === row.sourceIndex
            ? {
                ...project,
                name: values.item,
                productNotes: values.item,
                jobcost_quantity: finalQty,
                ...(isTM && { jobcost_actual_quantity: finalQty }),
                quantity: finalQty,
                jobcost_unit_cost: calculated.unitCost,
                jobcost_total: calculated.cost,
                jobcost_bill_rate: calculated.billRate,
                jobcost_total_bill: calculated.bill,
                markup: calculated.markup,
                bill_rate: calculated.billRate,
                billable: calculated.billable ? 1 : 0,
                jobcost_timestamp: Math.floor(Date.now() / 1000),
                taken
              }
            : project
        )
      );

      setEditingUid(null);
    }
  });

  const handleEditRow = (row) => {
    setEditingUid(row.uid);
    setLastEditedPriceField(PRICE_SOURCE.MARKUP);
    formik.setValues({
      item: row.item,
      qty: round2(row.qty),
      unitCost: row.unitCost,
      markup: row.markup,
      billRate: row.billRate,
      billable: row.billable
    });
  };

  const updateFormikCalculatedValues = (nextValues, nextPriceSource) => {
    const calculated = calculateRowValues({
      qty: nextValues.qty,
      unitCost: nextValues.unitCost,
      markup: nextValues.markup,
      billRate: nextValues.billRate,
      billable: nextValues.billable,
      // Backup before T&M material markup: priceSource: nextPriceSource
      priceSource: nextPriceSource,
      costIncludesMarkup: isTM
    });
    formik.setValues({
      ...nextValues,
      qty: calculated.qty,
      unitCost: calculated.unitCost,
      markup: calculated.markup,
      billRate: calculated.billRate,
      billable: calculated.billable
    });
  };

  const handleDelete = (row) => {
    setItemToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    // Backup: normal material delete only updated local state (did not hit API), so rows reappeared on refresh
    // if (itemToDelete.type === 'material') {
    //   setSelectedProjects((prev) => prev.filter((_, index) => index !== itemToDelete.sourceIndex));
    //   if (onMaterialsRefresh) onMaterialsRefresh();
    //   setDeleteDialogOpen(false);
    //   setItemToDelete(null);
    //   return;
    // }
    //
    // if (itemToDelete.type === 'additional') {
    //   const jobCostId = itemToDelete.raw?.job_cost_id ?? itemToDelete.raw?.product_id;
    //   if (!jobCostId || !userId) {
    //     enqueueSnackbar('Cannot delete this row', { variant: 'error' });
    //     setDeleteDialogOpen(false);
    //     setItemToDelete(null);
    //     return;
    //   }
    //   try {
    //     const res = await axios.post(
    //       `${REST_API_END_POINT}project/job-cost-delete-item`,
    //       { jobCostId, user_id: userId },
    //       { headers: { 'Content-Type': 'application/json' } }
    //     );
    //     if (res?.data?.status === 1) {
    //       enqueueSnackbar(res.data.message || 'Removed', { variant: 'success' });
    //       if (onMaterialsRefresh) onMaterialsRefresh();
    //     } else {
    //       enqueueSnackbar(res?.data?.message || 'Delete failed', { variant: 'error' });
    //     }
    //   } catch (e) {
    //     enqueueSnackbar('Delete failed', { variant: 'error' });
    //   }
    //   setDeleteDialogOpen(false);
    //   setItemToDelete(null);
    // }

    if (itemToDelete.type === 'material' || itemToDelete.type === 'additional') {
      // Backup (before additional-material scan-delete fix):
      // const jobCostId =
      //   itemToDelete.raw?.job_cost_id ??
      //   (itemToDelete.type === 'additional' ? itemToDelete.raw?.product_id : itemToDelete.raw?.id);
      const jobCostId = itemToDelete.raw?.job_cost_id;
      if (!jobCostId || !userId) {
        enqueueSnackbar('Cannot delete this row', { variant: 'error' });
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        return;
      }
      try {
        const res = await axios.post(
          `${REST_API_END_POINT}project/job-cost-delete-item`,
          { jobCostId, user_id: userId },
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (res?.data?.status === 1) {
          enqueueSnackbar(res.data.message || 'Removed', { variant: 'success' });
          if (itemToDelete.type === 'material') {
            setSelectedProjects((prev) => prev.filter((_, index) => index !== itemToDelete.sourceIndex));
          }
          if (onMaterialsRefresh) onMaterialsRefresh();
        } else {
          enqueueSnackbar(res?.data?.message || 'Delete failed', { variant: 'error' });
        }
      } catch (e) {
        enqueueSnackbar('Delete failed', { variant: 'error' });
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <Box sx={{ mt: 3, pl: 2, pr: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Tooltip title={`Total Cost = Sum of all Cost values = $${summaryStats.totalCost.toFixed(2)}`} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'transparent', px: 2, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Cost: ${summaryStats.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Tooltip>
        {/* {!isTM && (
          <Tooltip title={`Total Billable = Sum of Bill where Billable is checked = $${summaryStats.totalBillable.toFixed(2)}`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'primary.main', px: 2, py: 1, borderRadius: 1 }}>
              <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'common.white' }} />
              <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 800 }}>
                Bill: ${summaryStats.totalBillable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {!isTM && (
          <Tooltip
            title={`Margin % = ((Billable - Cost) / Cost) x 100 = (($${summaryStats.totalBillable.toFixed(2)} - $${summaryStats.totalCost.toFixed(2)}) / $${summaryStats.totalCost.toFixed(2)}) x 100 = ${summaryStats.marginPercent.toFixed(2)}%`}
            arrow
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: (t) =>
                  summaryStats.marginPercent < 0
                    ? (t.palette.mode === 'dark' ? `${t.palette.error.main}1a` : '#ffebee')
                    : (t.palette.mode === 'dark' ? `${t.palette.success.main}1a` : '#e8f5e9'),
                px: 2,
                py: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: summaryStats.marginPercent < 0 ? 'error.light' : 'success.light'
              }}
            >
              <Box
                component={'span'}
                className={summaryStats.marginPercent < 0 ? 'ph--trend-down' : 'ph--trend-up'}
                width={18}
                height={18}
                sx={{ mr: 1, color: summaryStats.marginPercent < 0 ? 'error.main' : '#54D62C' }}
              />
              <Typography variant="subtitle2" sx={{ color: summaryStats.marginPercent < 0 ? 'error.dark' : 'success.dark', fontWeight: 800 }}>
                Margin: {summaryStats.marginPercent.toFixed(2)}%
              </Typography>
            </Box>
          </Tooltip>
        )} */}
      </Stack>
      <Box sx={{ width: "100%", display: "flex", alignItems: 'center', justifyContent: "flex-end", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: "#ffffff", borderRadius: "20%", border: "1px solid black" }} />
            <Typography variant="body2">Materials</Typography>
            <Tooltip title={`Materials count: ${materialsCount}`} arrow>
              <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'help' }}>
                ({materialsCount})
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: "#ffc0cb", borderRadius: "20%", border: "1px solid black" }} />
            <Typography variant="body2">Additional Materials</Typography>
            <Tooltip title={`Additional materials count: ${additionalMaterialsCount}`} arrow>
              <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'help' }}>
                ({additionalMaterialsCount})
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Card sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, overflow: 'hidden' }}>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 700 }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {TABLE_HEAD.map((head) => {
                    if (['billRate', 'bill', 'billable'].includes(head.id)) return null;
                    if (isTM && ['markup', 'Expt Qty'].includes(head.id)) return null;
                    return (
                      <TableCell
                        key={head.id}
                        align={head.align}
                        sx={{ fontWeight: 800, color: 'text.secondary', py: 1.8, fontSize: '0.8rem', textTransform: 'uppercase' }}
                      >
                        {head.label}
                      </TableCell>
                    );
                  })}
                  {/* {TABLE_HEAD.filter(head => {
                    if (['billRate', 'bill', 'billable'].includes(head.id)) return false;
                    if (isTM && ['markup'].includes(head.id)) return false;
                    return true;
                  }).map((head) => (
                    <TableCell
                      key={head.id}
                      align={head.align}
                      sx={{ fontWeight: 800, color: 'text.secondary', py: 1.8, fontSize: '0.8rem', textTransform: 'uppercase' }}
                    >
                      {head.label}
                    </TableCell>
                  ))} */}
                </TableRow>

                <AnimatePresence>
                {isLoading ? (
                  [...Array(4)].map((_, i) => (
                    <TableRow key={i}>
                      {/* {[...Array(TABLE_HEAD.filter(h => !isTM || !['markup', 'billRate', 'bill', 'billable'].includes(h.id)).length)].map((__, j) => ( */}
                      {[...Array(TABLE_HEAD.filter(h => {
                        if (['billRate', 'bill', 'billable'].includes(h.id)) return false;
                        if (isTM && ['markup', 'Expt Qty'].includes(h.id)) return false;
                        return true;
                      }).length)].map((__, j) => (
                        <TableCell key={j}><Skeleton variant="text" width="60%" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : 
                 rows.map((row, index) => {
                  console.log("sghdhsghsgdsdgsjdgsjdgs",row);
                  const isEditing = editingUid === row.uid;
                  const effectiveBillable = normalizeBillable(row.billable);
                  const effectiveBill = toSafeNumber(row.bill);
                  const isAdditionalRow = row.type === 'additional';
                  const rowTextColor = isAdditionalRow ? '#ffc0cb' : theme.palette.common.white;
                  const nameColor = rowTextColor;
                  const barcodeColor = rowTextColor;
                  // Stock left = current inventory qty (material_inventory.qty), not available_stock
                  // (available_stock is mi.qty + jc.quantity, which incorrectly showed 2+3=5).
                  // Additional rows: `id` is inventory id (`product_id` is the job_cost row id).
                  // Regular rows: prefer `product_id` (inventory id); `id` can be job_cost id on some payloads (e.g. RFP).
                  const maxAllowed = (() => {
                    const raw = row?.raw || {};
                    const currentId = isAdditionalRow
                      ? (raw.id ?? raw.product_id)
                      : (raw.product_id ?? raw.id);
                    if (currentId == null || currentId === '') {
                      console.log('[Stock] missing material id', {
                        materialType: row.type,
                        rawId: raw.id,
                        rawProductId: raw.product_id,
                        orderedQty: row.qty
                      });
                      return '';
                    }

                    const numericId = Number(currentId);
                    if (!Number.isFinite(numericId)) {
                      console.log('[Stock] invalid material id', {
                        materialType: row.type,
                        currentId,
                        orderedQty: row.qty
                      });
                      return '';
                    }

                    const productRecord = (productList || []).find(
                      (item) => Number(item?.id) === numericId
                    );

                    // Prefer live inventory qty from productList; fall back to qty on the row payload.
                    const inventoryQty = Number(
                      productRecord?.qty ?? productRecord?.quantity ?? raw.qty ?? raw.quantity
                    );
                    const calculatedStock = Number.isFinite(inventoryQty) ? inventoryQty : 0;

                    console.log('[Stock] inventory qty lookup', {
                      materialId: numericId,
                      materialType: row.type,
                      barcode: raw.barcode,
                      orderedQty: Number(row.qty),
                      available_stock_ignored: raw.available_stock,
                      matchedProductRecord: productRecord
                        ? { id: productRecord.id, qty: productRecord.qty, actual_qty: productRecord.actual_qty }
                        : null,
                      rawQty: raw.qty,
                      calculatedStock
                    });

                    return calculatedStock;
                  })();
                  // Live stock: inventory minus any qty increase while editing (add 1 → stock drops by 1).
                  const qtyDelta = isEditing
                    ? toSafeNumber(formik.values.qty) - toSafeNumber(row.qty)
                    : 0;
                  const stockLeft = (() => {
                    if (maxAllowed === '') return '';
                    const baseStock = Number(maxAllowed);
                    if (!Number.isFinite(baseStock)) return '';
                    const liveStock = isTM ? baseStock : Math.max(0, baseStock - qtyDelta);
                    const displayStock = liveStock.toFixed(2);
                    console.log('[Stock] display value', {
                      materialType: row.type,
                      materialName: row.item,
                      maxAllowed,
                      qtyDelta,
                      stockLeft: displayStock
                    });
                    return displayStock;
                  })();
                  return (
                    <TableRow
                      key={row.uid}
                      component={motion.tr}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        borderBottom: (t) => `solid 1px ${t.palette.divider}`,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <TableCell sx={{ color: rowTextColor }}>{row.date}</TableCell>
                      {isEditing ? (
                        <FormikProvider value={formik}>
                          <>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: rowTextColor }}>
                                {formik.values.item}
                              </Typography>
                              {/* Old code:
                              {isTM ? (
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: rowTextColor }}>
                                  {formik.values.item}
                                </Typography>
                              ) : (
                                <TextField size="small" fullWidth value={formik.values.item} onChange={(e) => formik.setFieldValue('item', e.target.value)} />
                              )}
                              */}
                            {!!stockLeft && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'error.main', whiteSpace: 'nowrap' }}>
                                Stock left: {stockLeft}
                              </Typography>
                            )}
                            </TableCell>
                            {!isTM && (
                              <TableCell align="center" sx={{ color: rowTextColor, verticalAlign: 'middle' }}>
                                <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {/* Backup before expected qty display fix: row?.raw?.match_actual_quantity != null ? Number(row?.raw?.match_actual_quantity).toFixed(2) : '-' */}
                                  {formatExpectedQtyDisplay(row?.raw)}
  
                                  {/* display the unit of the stock with the qty */}
                                  {UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name && (
                                    <span> ({UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name})</span>
                                  )}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  variant="outlined"
                                  label={UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name && (
                                  <span> ({UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name})</span>
                                )}
                                  placeholder=  {UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name && (
                                  <span> ({UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name})</span>
                                )}
                                  InputLabelProps={{ shrink: true }}
                                  error={!isTM && maxAllowed !== '' && Number(formik.values.qty-row.qty) > maxAllowed}
                                  value={formik.values.qty}
                                  onKeyDown={preventInvalidNumberKeys}
                                  inputProps={{ min: 0, step: 1 }}
                                  onChange={(e) => {
                                    const nextValues = { ...formik.values, qty: e.target.value };
                                    updateFormikCalculatedValues(nextValues, lastEditedPriceField);
                                  }}
                                  sx={{ width: 80, mx: 'auto' }}
                                />
                                {!isTM && maxAllowed !== '' && Number(formik.values.qty-row.qty) > maxAllowed && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5, whiteSpace: 'nowrap' }}>
                                    stock quantity is exceeds the stock
                                  </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: rowTextColor }}>
                                ${Number(formik.values.unitCost || 0).toFixed(2)}
                              </Typography>
                              {/* Old code:
                              {isTM ? (
                                <Typography variant="subtitle2" sx={{ color: rowTextColor }}>
                                  ${Number(formik.values.unitCost || 0).toFixed(2)}
                                </Typography>
                              ) : (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={formik.values.unitCost}
                                  onKeyDown={preventInvalidNumberKeys}
                                  inputProps={{ min: 0 }}
                                  onChange={(e) => {
                                    const nextValues = { ...formik.values, unitCost: e.target.value };
                                    updateFormikCalculatedValues(nextValues, lastEditedPriceField);
                                  }}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  sx={{ width: 100 }}
                                />
                              )}
                              */}
                            </TableCell>
                           
                            {!isTM && (
                              <TableCell>
                                {/* <Tooltip
                                  title={getMarkupTooltip(formik.values.unitCost, formik.values.billRate, formik.values.markup)}
                                  arrow
                                > */}
                                  <>
                                    <Typography variant="subtitle2" sx={{ color: rowTextColor }}>
                                      {Number(formik.values.markup || 0).toFixed(0)}%
                                    </Typography>
                                    {/* <TextField
                                      size="small"
                                      type="number"
                                      value={formik.values.markup}
                                      onKeyDown={preventInvalidNumberKeys}
                                      inputProps={{ min: 0 }}
                                      onChange={(e) => {
                                        setLastEditedPriceField(PRICE_SOURCE.MARKUP);
                                        const nextValues = { ...formik.values, markup: e.target.value };
                                        updateFormikCalculatedValues(nextValues, PRICE_SOURCE.MARKUP);
                                      }}
                                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                      sx={{ width: 90 }}
                                    /> */}
                                  </>
                                {/* </Tooltip> */}
                              </TableCell>
                            )}
                            {/* {!isTM && (
                              <TableCell>
                                <Tooltip
                                  title={getBillRateTooltip(formik.values.unitCost, formik.values.markup, formik.values.billRate)}
                                  arrow
                                >
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={formik.values.billRate}
                                    onKeyDown={preventInvalidNumberKeys}
                                    inputProps={{ min: 0 }}
                                    onChange={(e) => {
                                      setLastEditedPriceField(PRICE_SOURCE.BILL_RATE);
                                      const nextValues = { ...formik.values, billRate: e.target.value };
                                      updateFormikCalculatedValues(nextValues, PRICE_SOURCE.BILL_RATE);
                                    }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ width: 100 }}
                                  />
                                </Tooltip>
                              </TableCell>
                            )} */}
                          
                            <TableCell>
                              <Tooltip
                                title={getCostTooltip(
                                  formik.values.qty,
                                  formik.values.unitCost,
                                  calculateRowValues({
                                    qty: formik.values.qty,
                                    unitCost: formik.values.unitCost,
                                    markup: formik.values.markup,
                                    billRate: formik.values.billRate,
                                    billable: formik.values.billable,
                                    // Backup before T&M material markup: priceSource: lastEditedPriceField
                                    priceSource: lastEditedPriceField,
                                    costIncludesMarkup: isTM
                                  }).cost,
                                  formik.values.markup,
                                  isTM
                                )}
                                arrow
                              >
                                <span>
                                  ${calculateRowValues({
                                    qty: formik.values.qty,
                                    unitCost: formik.values.unitCost,
                                    markup: formik.values.markup,
                                    billRate: formik.values.billRate,
                                    billable: formik.values.billable,
                                    // Backup before T&M material markup: priceSource: lastEditedPriceField
                                    priceSource: lastEditedPriceField,
                                    costIncludesMarkup: isTM
                                  }).cost.toFixed(2)}
                                </span>
                              </Tooltip>
                            </TableCell>
                            {/* {!isTM && (
                              <TableCell>
                                <Tooltip
                                  title={getBillTooltip(
                                    formik.values.qty,
                                    formik.values.billRate,
                                    formik.values.billable,
                                    calculateRowValues({
                                      qty: formik.values.qty,
                                      unitCost: formik.values.unitCost,
                                      markup: formik.values.markup,
                                      billRate: formik.values.billRate,
                                      billable: formik.values.billable,
                                      priceSource: lastEditedPriceField
                                    }).bill
                                  )}
                                  arrow
                                >
                                  <span>
                                    ${calculateRowValues({
                                      qty: formik.values.qty,
                                      unitCost: formik.values.unitCost,
                                      markup: formik.values.markup,
                                      billRate: formik.values.billRate,
                                      billable: formik.values.billable,
                                      priceSource: lastEditedPriceField
                                    }).bill.toFixed(2)}
                                  </span>
                                </Tooltip>
                              </TableCell>
                            )}
                            {!isTM && (
                              <TableCell align="center">
                                <Tooltip title={getBillableTooltipMaterial(formik.values.billable)} arrow>
                                  <span>
                                    <Checkbox
                                      checked={Boolean(formik.values.billable)}
                                      onChange={(e) => {
                                        const nextValues = { ...formik.values, billable: e.target.checked };
                                        updateFormikCalculatedValues(nextValues, lastEditedPriceField);
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </TableCell>
                            )} */}
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={savingAdditional}
                                  onClick={!isTM && maxAllowed !== '' && Number(formik.values.qty-row.qty) > maxAllowed ? undefined : formik.handleSubmit}
                                >
                                  <Icon icon={saveFill} width={18} height={18} />
                                </IconButton>
                                <IconButton size="small" color="inherit" onClick={() => setEditingUid(null)}>
                                  <Icon icon={closeFill} width={18} height={18} />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </>
                        </FormikProvider>
                      ) : (
                        <>
                          <TableCell sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {row.raw?.finalTaken === 1 && projectData?.syncWithQuickbooks === 1 && (
                                <Avatar
                                  src={QbLogo}
                                  alt="QB"
                                  sx={{ width: 24, height: 24, objectFit: 'cover' }}
                                />
                              )}
                              <Tooltip title={row.item || ''} arrow>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: nameColor,
                                    maxWidth: 180,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {row.item}
                                </Typography>
                              </Tooltip>
                            </Stack>
                            {!!row.barcode && (
                              <Typography variant="caption" sx={{ display: 'block', color: barcodeColor }}>
                                {row.barcode}
                              </Typography>
                            )}
                            {!!stockLeft && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'error.main', whiteSpace: 'nowrap' }}>
                                Stock left: {stockLeft}
                              </Typography>
                            )}
                          </TableCell>
                          {!isTM && (
                            <TableCell align="center" sx={{ color: rowTextColor, verticalAlign: 'middle' }}>
                              <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {/* Backup before expected qty display fix: row?.raw?.match_actual_quantity||row?.raw?.jobcost_actual_quantity || '-' */}
                              {formatExpectedQtyDisplay(row?.raw)}
  
                              {/* need to show the unit of the stock   with the qty */ }
                              {/* {row?.raw?.convertion_unit } */}
                              {/* take the unit from the defaltvale and map this and show the unit short_name */}
                              {UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name && (
                                <span> ({UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name})</span>
                              )}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ color: rowTextColor, verticalAlign: 'middle' }}>
                            <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {Number(row.qty || 0).toFixed(2)}
                              {/* need to show the unit of the stock   with the qty */ }
                              {UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name && (
                                <span> ({(UNITS.find((unit) => unit.id === row?.raw?.convertion_unit)?.short_name)})</span>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: rowTextColor }}>${Number(row.unitCost || 0).toFixed(2)}</TableCell>
                          {!isTM && (
                            <>
                              <TableCell sx={{ color: rowTextColor }}>
                                {/* <Tooltip title={getMarkupTooltip(row.unitCost, row.billRate, row.markup)} arrow> */}
                                  <span>{Number(row.markup || 0).toFixed(0)}%</span>
                                {/* </Tooltip> */}
                              </TableCell>
                              {/* <TableCell sx={{ color: rowTextColor }}>
                                <Tooltip title={getBillRateTooltip(row.unitCost, row.markup, row.billRate)} arrow>
                                  <span>${Number(row.billRate || 0).toFixed(2)}</span>
                                </Tooltip>
                              </TableCell> */}
                            </>
                          )}
                          <TableCell sx={{ color: rowTextColor }}>
                            <Tooltip title={getCostTooltip(row.qty, row.unitCost, row.cost, row.markup, isTM)} arrow>
                              <span>${Number(row.cost || 0).toFixed(2)}</span>
                            </Tooltip>
                          </TableCell>
                          {/* {!isTM && (
                            <>
                              <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>
                                <Tooltip title={getBillTooltip(row.qty, row.billRate, effectiveBillable, effectiveBill)} arrow>
                                  <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                    ${Number(effectiveBill || 0).toFixed(2)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={getBillableTooltipMaterial(row.billable)} arrow>
                                  <Box sx={{
                                    width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: effectiveBillable ? 'success.main' : 'error.main', mx: 'auto'
                                  }}>
                                    <Icon icon={effectiveBillable ? checkmarkCircle2Fill : closeCircleFill} width={14} height={14} style={{ color: 'white' }} />
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            </>
                          )} */}
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <IconButton
                                size="small"
                                onClick={() => handleEditRow(row)}
                                disabled={isMirrored}
                              >
                                <Icon icon={edit2Fill} width={18} height={18} />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(row)}
                                disabled={isMirrored}
                              >
                                <Icon icon={trash2Fill} width={18} height={18} />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No material entries</Typography>
                    </TableCell>
                  </TableRow>
                )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2, mb: 2 }}>
        <Button
          variant="text"
          onClick={() => {
            if (onAddMaterialEntry) onAddMaterialEntry();
          }}
          startIcon={<Box component="span" sx={{ fontSize: 20, lineHeight: 1, fontWeight: 500 }}>+</Box>}
          sx={{ pl: 1, color: 'primary.main', fontWeight: 700 }}
        >
          Add Material Entry
        </Button>
        <Tooltip title={isTM ?"The stock will be deducted for T&M only after we approve the final invoice." : "The material quantity will update according to the expected quantity. Once the expected quantity is reached, the available quantity will decrease accordingly."} placement="top" arrow>
          <IconButton size="small" sx={{ color: 'text.secondary' }} aria-label="QuickBooks job cost accounting info">
            <Info fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <ConfirmationPopup
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        message={
          itemToDelete?.type === 'additional'
            ? 'Remove this additional material from job cost?'
            : 'Are you sure you want to remove this material entry?'
        }
        handleClick={handleDeleteConfirm}
      />
    </Box>
  );
}
